import { ChevronDownIcon, FileTextIcon, PlusIcon, SettingsIcon, SparklesIcon, UserRoundIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/auth';
import { builtinValues, contactExampleValues, isContactVariable, renderTemplate, variableLabel, type TemplateVariable } from '@/lib/templates';
import type { JobDto, MessageTemplateDto } from '@/lib/types';
import { useJobTemplates, useMarkTemplateUsed } from './queries';

/** Anything the picker can insert: a saved template. */
interface Pickable {
  id?: string;
  name: string;
  body: string;
  variables: TemplateVariable[];
}

/**
 * The "Start from…" menu on Compose: the job's saved templates (its own and the organization's), grouped by
 * category. Built-in variables are filled from the job and the sender as the text is inserted, per-recipient ones
 * (`{{contact.firstName}}`) stay in the text for the API to fill per person, and anything else is asked for in a
 * small dialog first. `autoPickId` opens a template straight away (Templates page → "Use"). With no templates yet,
 * the menu points at creating one, or at "Save as template" beside it.
 */
export function TemplatePicker({ job, onInsert, canManage, autoPickId, onAutoPicked }: { job: JobDto; onInsert: (text: string) => void; canManage: boolean; autoPickId?: string | null; onAutoPicked?: () => void }) {
  const me = useMe();
  const templates = useJobTemplates(job.id);
  const markUsed = useMarkTemplateUsed(job.id);
  const [filling, setFilling] = useState<Pickable | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const items = templates.data?.items ?? [];
  const groups = groupByCategory(items);
  const picked = useRef<string | null>(null);

  const insert = (t: Pickable, custom: Record<string, string>) => {
    onInsert(renderTemplate(t.body, { ...builtinValues(job, me), ...custom }, { keep: isContactVariable }));
    if (t.id) markUsed.mutate(t.id);
  };

  const pick = (t: Pickable) => {
    const custom = t.variables.filter((v) => v.kind === 'custom');
    if (custom.length === 0) {
      insert(t, {});
      return;
    }
    setValues(Object.fromEntries(custom.map((v) => [v.key, ''])));
    setFilling(t);
  };

  // Deep link from the Templates page: open the requested template once the list is in.
  useEffect(() => {
    if (!autoPickId || picked.current === autoPickId) return;
    const t = items.find((x) => x.id === autoPickId);
    if (!t) return;
    picked.current = autoPickId;
    pick(t);
    onAutoPicked?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPickId, items]);

  const finish = () => {
    if (!filling) return;
    insert(filling, values);
    setFilling(null);
  };

  const custom = filling?.variables.filter((v) => v.kind === 'custom') ?? [];
  const personalized = filling?.variables.some((v) => v.kind === 'contact') ?? false;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm">
            <SparklesIcon /> Start from…
            <ChevronDownIcon className="hidden opacity-60 sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[420px] w-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-2 py-3 text-sm">
              <p className="font-medium">No templates yet</p>
              <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{templates.isPending ? 'Loading…' : canManage ? 'Write a message, then use “Save as template” to keep it for next time, or create one on the Templates page.' : 'A job admin can save messages as templates for this job.'}</p>
            </div>
          ) : (
            groups.map(([category, list]) => (
              <div key={category}>
                <DropdownMenuLabel className="flex items-center gap-1.5">
                  <FileTextIcon className="size-3.5 opacity-60" aria-hidden /> {category}
                </DropdownMenuLabel>
                {list.map((t) => (
                  <PickItem key={t.id} t={t} onPick={() => pick(t)} />
                ))}
              </div>
            ))
          )}
          <DropdownMenuSeparator />
          {canManage && (
            <DropdownMenuItem asChild>
              <Link to={`/jobs/${job.id}/templates?new=1`}>
                <PlusIcon /> New template
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link to={`/jobs/${job.id}/templates`}>
              <SettingsIcon /> {canManage ? 'Manage templates' : 'See all templates'}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!filling} onOpenChange={(o) => !o && setFilling(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-normal tracking-tight">{filling?.name}</DialogTitle>
            <DialogDescription>Fill in the blanks. Everything else comes from the job, from you{personalized ? ' and from each recipient' : ''}.</DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              finish();
            }}
          >
            {custom.map((v, i) => (
              <Field key={v.key} label={variableLabel(v.key)} htmlFor={`var-${v.key}`}>
                <Input id={`var-${v.key}`} autoFocus={i === 0} value={values[v.key] ?? ''} onChange={(e) => setValues((prev) => ({ ...prev, [v.key]: e.target.value }))} placeholder={`{{${v.key}}}`} />
              </Field>
            ))}
            {filling && (
              <div className="grid gap-1.5">
                <div className="bg-muted/40 border p-3 text-sm leading-relaxed whitespace-pre-wrap">
                  {renderTemplate(filling.body, { ...builtinValues(job, me), ...contactExampleValues(), ...Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v || `[${k}]`])) })}
                </div>
                {personalized && (
                  <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
                    <UserRoundIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden /> Shown for a sample recipient. Names, companies and titles are filled in for each person when it sends.
                  </p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFilling(null)}>
                Cancel
              </Button>
              <Button type="submit">Insert</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PickItem({ t, onPick }: { t: Pickable; onPick: () => void }) {
  const fillIn = t.variables.some((v) => v.kind === 'custom');
  const personal = t.variables.some((v) => v.kind === 'contact');
  return (
    <DropdownMenuItem onClick={onPick} className="flex-col items-start gap-0.5 py-2">
      <span className="flex w-full items-center gap-2">
        <span className="font-medium">{t.name}</span>
        <span className="text-muted-foreground ml-auto flex items-center gap-1.5 text-[10px] tracking-wide uppercase">
          {personal && (
            <span className="flex items-center gap-0.5" title="Personalized for each recipient">
              <UserRoundIcon className="size-3" aria-hidden /> personal
            </span>
          )}
          {fillIn && <span>fill-in</span>}
        </span>
      </span>
      <span className="text-muted-foreground line-clamp-1 text-xs">{t.body}</span>
    </DropdownMenuItem>
  );
}

function groupByCategory(items: MessageTemplateDto[]): Array<[string, MessageTemplateDto[]]> {
  const map = new Map<string, MessageTemplateDto[]>();
  for (const t of items) {
    const key = t.category?.trim() || 'Templates';
    const list = map.get(key) ?? [];
    list.push(t);
    map.set(key, list);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
}
